<template>
  <div class="overlay">
    <div class="overlay__inner">
      <div class="panel">
        <div class="panel__header">
          <h3 class="panel__title">Start fresh for a new consignor</h3>

          <span class="overlay__close" @click="done">
            <i class="fal fa-times"></i>
          </span>
        </div>

        <div class="panel__content">
          <!-- Finished: confirm and offer undo -->
          <div v-if="result">
            <p class="notice notice--success">
              Moved {{ result.line_item_count }} sale(s) and {{ result.payout_count }} payout(s)
              ({{ formatMoney(result.commission_total) }}) to the hidden vendor
              "{{ result.archive_vendor_name }}". {{ vendor.name }} now starts from zero.
            </p>
            <p>Nothing was deleted. If this wasn't right, you can undo it here or later from this vendor's page.</p>
            <p v-if="error" class="notice notice--error">{{ error }}</p>
            <div class="panel__footer text-right">
              <button class="button--alt" :disabled="saving" @click="undo(result)">Undo</button>
              <button @click="done">Done</button>
            </div>
          </div>

          <div v-else>
            <p>
              Use this when you give this vendor number to a new consignor. The previous consignor's
              sales and payouts move to a hidden vendor, so the new consignor's account starts from zero.
              Nothing is deleted, and you can undo it.
            </p>

            <!-- Scope -->
            <div class="form-field">
              <label>
                <input type="radio" value="all" v-model="scope" @change="preview"> All history
              </label>
              <label>
                <input type="radio" value="before" v-model="scope"> Only history before
                <input type="date" class="date-input" v-model="before" @change="preview"
                       :disabled="scope !== 'before'">
              </label>
              <p class="help">Pick the date the new consignor started, so their own sales stay on this vendor.</p>
            </div>

            <p v-if="loading">Checking this vendor's history…</p>

            <div v-if="selection && !loading">
              <div v-if="selection.issues.length" class="notice notice--error">
                <p><strong>This can't be done yet:</strong></p>
                <ul>
                  <li v-for="issue in selection.issues" :key="issue.code">{{ issue.message }}</li>
                </ul>
              </div>

              <p v-if="selection.totals.line_items || selection.totals.payouts">
                <strong>{{ selection.totals.line_items }} sale(s)</strong> and
                <strong>{{ selection.totals.payouts }} payout(s)</strong>
                ({{ formatMoney(selection.totals.commission) }} commission) will move to a hidden vendor named
                "{{ selection.archive_name }}".
              </p>

              <table v-if="selection.line_items.length" class="archive-table">
                <thead>
                  <tr><th>Sold</th><th>Order</th><th>Product</th><th>Commission</th><th>Paid in invoice</th></tr>
                </thead>
                <tbody>
                  <tr v-for="li in selection.line_items" :key="li.id"
                      :class="{'is-flagged': flaggedLine(li)}">
                    <td>{{ formatDay(li.processed_at) }}</td>
                    <td>{{ li.order_name }}</td>
                    <td>{{ li.title }}</td>
                    <td>{{ formatMoney(li.commission_amount) }}</td>
                    <td>{{ li.vendor_paid ? ('#' + (li.payout_invoice_number || '—')) : 'Not paid' }}</td>
                  </tr>
                </tbody>
              </table>

              <table v-if="selection.payouts.length" class="archive-table">
                <thead>
                  <tr><th>Payout date</th><th>Invoice</th><th>Amount</th></tr>
                </thead>
                <tbody>
                  <tr v-for="p in selection.payouts" :key="p.id">
                    <td>{{ formatDay(p.created_date) }}</td>
                    <td>#{{ p.invoice_number }}</td>
                    <td>{{ formatMoney(p.amount) }}</td>
                  </tr>
                </tbody>
              </table>

              <div v-if="selection.live_products.count" class="notice">
                {{ selection.live_products.count }} product(s) are still assigned to {{ vendor.name }}
                and will stay with it. If any belong to the previous consignor, remove or re-tag them in Shopify.
                <ul class="product-list">
                  <li v-for="p in selection.live_products.sample" :key="p.id">
                    {{ p.title }} <span class="help">(added {{ formatDay(p.created_at) }})</span>
                  </li>
                </ul>
              </div>

              <div v-if="selection.can_apply">
                <div class="form-field">
                  <label for="archive-previous-name">Previous consignor's name</label>
                  <input id="archive-previous-name" type="text" v-model="previousName"
                         placeholder="Optional">
                  <p class="help">Shown on the hidden vendor so you know whose history it is. Leave blank if unsure.</p>
                </div>
                <div class="form-field">
                  <label for="archive-previous-email">Previous consignor's email</label>
                  <input id="archive-previous-email" type="text" v-model="previousEmail"
                         placeholder="Optional">
                </div>
                <div class="form-field">
                  <label for="archive-confirm">Type <strong>{{ vendor.name }}</strong> to confirm</label>
                  <input id="archive-confirm" type="text" v-model="confirmName" autocomplete="off">
                </div>
              </div>
            </div>

            <p v-if="error" class="notice notice--error">{{ error }}</p>

            <div class="panel__footer text-right">
              <button class="button--alt" @click="done">Cancel</button>
              <button :disabled="!canSubmit" @click="apply">
                {{ saving ? 'Moving…' : 'Move history' }}
              </button>
            </div>

            <!-- Earlier runs -->
            <div v-if="history.length" class="archive-history">
              <h4>Earlier resets for this vendor</h4>
              <ul>
                <li v-for="run in history" :key="run.id">
                  {{ formatDay(run.created_date) }}: {{ run.line_item_count }} sale(s),
                  {{ run.payout_count }} payout(s) moved to "{{ run.archive_vendor_name }}"
                  <span v-if="run.restored_date" class="help">(undone {{ formatDay(run.restored_date) }})</span>
                  <button v-else class="button--alt button--small" :disabled="saving"
                          @click="undo(run)">Undo</button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import client from '@/mixins/client'
import utils from '@/mixins/utils'

export default {
  // vendorId comes from the route: the vendor object on the page has no id.
  props: ['vendorId', 'vendor'],
  mixins: [client, utils],
  data: function () {
    return {
      scope: 'all',
      before: '',
      selection: null,
      loading: false,
      saving: false,
      error: '',
      previousName: '',
      previousEmail: '',
      confirmName: '',
      result: null,
      history: [],
      previewSeq: 0
    }
  },
  computed: {
    baseUrl () {
      return '/vendors/' + this.vendorId + '/archive-history/'
    },
    canSubmit () {
      return !!(this.selection && this.selection.can_apply && !this.loading &&
        !this.saving && this.confirmName.trim() === String(this.vendor.name))
    },
    flaggedIds () {
      let ids = []
      if (!this.selection) return ids
      for (let issue of this.selection.issues) {
        ids = ids.concat(issue.line_item_ids || [])
      }
      return ids
    },
    movingInvoices () {
      return this.selection ? this.selection.payouts.map(p => p.invoice_number) : []
    }
  },
  methods: {
    done () {
      this.$emit('done')
    },
    errorMessage (error) {
      // client.js rejects with the response body: a DRF error has a detail,
      // anything else (an HTML error page) means the request itself failed.
      if (error && error.detail) return error.detail
      return 'The request failed (unexpected server response). Try again, or contact support.'
    },
    formatDay (value) {
      return value ? new Date(value).toLocaleDateString() : ''
    },
    flaggedLine (li) {
      // Unpaid, or paid by a payout that would stay behind (a split payout).
      if (this.flaggedIds.indexOf(li.id) !== -1) return true
      return !!(li.payout_invoice_number &&
        this.movingInvoices.indexOf(li.payout_invoice_number) === -1)
    },
    preview () {
      this.error = ''
      this.confirmName = ''
      if (this.scope === 'before' && !this.before) {
        this.selection = null
        return
      }
      let params = this.scope === 'before' ? {before: this.before} : {}
      // Ignore stale answers when the date is changed quickly.
      let seq = ++this.previewSeq
      this.loading = true
      this.request('get', this.baseUrl + 'preview/', params).then(response => {
        if (seq !== this.previewSeq) return
        this.loading = false
        this.selection = response
        if (!this.previousName) this.previousName = response.suggested_previous_name || ''
      }).catch(error => {
        if (seq !== this.previewSeq) return
        this.loading = false
        this.selection = null
        this.error = this.errorMessage(error)
      })
    },
    apply () {
      if (!this.canSubmit) return
      this.saving = true
      this.error = ''
      this.request('post', this.baseUrl, {
        before: this.scope === 'before' ? this.before : null,
        fingerprint: this.selection.fingerprint,
        confirm_name: this.confirmName.trim(),
        previous_contact_name: this.previousName.trim(),
        previous_email: this.previousEmail.trim()
      }).then(response => {
        this.saving = false
        this.result = response
        this.$emit('save')
      }).catch(error => {
        this.saving = false
        this.error = this.errorMessage(error)
        // The history changed or something now blocks it: show the current state.
        this.preview()
      })
    },
    undo (run) {
      this.saving = true
      this.error = ''
      this.request('post', '/vendors/archive-history/' + run.id + '/restore/', {}).then(response => {
        this.saving = false
        this.result = null
        this.selection = null
        this.$emit('save')
        this.loadHistory()
        this.preview()
      }).catch(error => {
        this.saving = false
        this.error = this.errorMessage(error)
      })
    },
    loadHistory () {
      this.request('get', this.baseUrl).then(response => {
        this.history = response || []
      }).catch(() => {
        this.history = []
      })
    }
  },
  created () {
    this.preview()
    this.loadHistory()
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/mixins";
@import "../styles/variables";

.panel__content {
  max-height: 80vh;
  overflow-y: auto;
}

.date-input {
  display: inline-block;
  width: auto;
  margin: 0 0 0 .5rem;
}

.help {
  font-size: .85rem;
  opacity: .7;
}

.notice {
  padding: .75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  background: rgba(0, 0, 0, .04);

  ul {
    margin-bottom: 0;
  }
}

.notice--error {
  background: rgba(204, 51, 51, .1);
  color: #a12626;
}

.notice--success {
  background: rgba(46, 160, 67, .1);
}

.archive-table {
  width: 100%;
  margin-bottom: 1rem;
  font-size: .9rem;

  th {
    text-align: left;
  }

  .is-flagged td {
    background: rgba(204, 51, 51, .1);
  }
}

.product-list {
  margin-top: .5rem;
}

.archive-history {
  margin-top: 2rem;

  li {
    margin-bottom: .5rem;
  }
}

button:disabled {
  opacity: .4;
  cursor: not-allowed;
}

.button--small {
  padding: .25rem .75rem;
  margin-left: .5rem;
}
</style>


// WEBPACK FOOTER //
// src/globals/archiveVendorHistory.vue